/**
 * Template-instance management for a single section, moved off the main page
 * into a modal opened from the section tab's three-dot menu ("Versions…").
 * Wraps the existing `SectionTemplatesTable` (active card + standby list +
 * create/generate/promote/pause/delete). "Edit" loads the chosen instance into
 * the inline editor and closes the dialog.
 */
import { useCallback, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import type { BespokeTemplateResponse } from '../../services/api/types.gen';
import type {
  FormTreeQuestion,
  SectionGenerationStatus,
} from './formSchemaTypes';
import { SectionTemplatesTable } from './SectionTemplatesTable';
import type { RemotePushInfo } from './formSchemaUtils';

type BespokeStatus = BespokeTemplateResponse['status'];

function isRadixMenuTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    (target.closest('[role="menu"]') != null ||
      target.closest('[data-radix-popper-content-wrapper]') != null)
  );
}

interface SectionVersionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sectionDisplayName: string;
  templates: BespokeTemplateResponse[];
  questions: FormTreeQuestion[];
  generationStatus?: SectionGenerationStatus;
  busyTemplateId?: string | null;
  /** MCP-push info for a row, or `null` (EMR-277). */
  getRemotePush: (templateId: string) => RemotePushInfo | null;
  onCreateLayout: () => void;
  onGenerateLayout: () => void;
  onGenerateLayoutWithCustomPrompt: () => void;
  onAddLayoutFromClipboard: () => void;
  onEditTemplate: (template: BespokeTemplateResponse) => void;
  onEditTemplateDetails: (
    template: BespokeTemplateResponse,
    details: { name: string; description: string }
  ) => Promise<void>;
  onCopyTemplate: (template: BespokeTemplateResponse) => void;
  onPasteTemplate: (template: BespokeTemplateResponse) => void;
  onSetTemplateStatus: (
    template: BespokeTemplateResponse,
    status: BespokeStatus
  ) => void;
  onDeleteTemplate: (template: BespokeTemplateResponse) => void;
}

export function SectionVersionsDialog({
  open,
  onOpenChange,
  sectionDisplayName,
  templates,
  questions,
  generationStatus,
  busyTemplateId,
  getRemotePush,
  onCreateLayout,
  onGenerateLayout,
  onGenerateLayoutWithCustomPrompt,
  onAddLayoutFromClipboard,
  onEditTemplate,
  onEditTemplateDetails,
  onCopyTemplate,
  onPasteTemplate,
  onSetTemplateStatus,
  onDeleteTemplate,
}: SectionVersionsDialogProps) {
  const [menuPortalContainer, setMenuPortalContainer] =
    useState<HTMLElement | null>(null);
  const dialogContentRef = useCallback((node: HTMLDivElement | null) => {
    setMenuPortalContainer(node);
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        ref={dialogContentRef}
        className='flex max-h-[min(90vh,52rem)] max-w-2xl flex-col'
        onInteractOutside={e => {
          if (isRadixMenuTarget(e.target)) {
            e.preventDefault();
          }
        }}
        onFocusOutside={e => {
          if (isRadixMenuTarget(e.target)) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader className='shrink-0'>
          <DialogTitle>Layout versions — {sectionDisplayName}</DialogTitle>
          <DialogDescription>
            Manage the active layout and standby alternates for this section.
            Validation shows whether each version passes section layout checks
            (hover Invalid for details). Editing a layout opens it in the
            section editor.
          </DialogDescription>
        </DialogHeader>
        <div className='min-h-0 flex-1 overflow-y-auto pr-1'>
          <SectionTemplatesTable
            menuPortalContainer={menuPortalContainer}
            sectionDisplayName={sectionDisplayName}
            templates={templates}
            questions={questions}
            generationStatus={generationStatus}
            busyTemplateId={busyTemplateId}
            getRemotePush={getRemotePush}
            onCreateLayout={onCreateLayout}
            onGenerateLayout={onGenerateLayout}
            onGenerateLayoutWithCustomPrompt={onGenerateLayoutWithCustomPrompt}
            onAddLayoutFromClipboard={onAddLayoutFromClipboard}
            onEditTemplate={template => {
              onEditTemplate(template);
              onOpenChange(false);
            }}
            onEditTemplateDetails={onEditTemplateDetails}
            onCopyTemplate={onCopyTemplate}
            onPasteTemplate={onPasteTemplate}
            onSetTemplateStatus={onSetTemplateStatus}
            onDeleteTemplate={onDeleteTemplate}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
